import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { datasetsApi, rawApi } from '../lib/api';
import { parseGeoText } from '../lib/geo-parsers';
import { analyzeTabularFile, resolveUtmFromTable } from '../lib/auto-detect';
import { buildXYZGeoJSON, describeSkipped } from '../lib/xyz-geojson';
import { useOptionalToast } from '../components/Toast';
import { useViewSync } from '../contexts/ViewSyncContext';
import { geojsonBounds } from '../lib/layer-tree';
import { chooseLayerMode } from '../lib/vector-tiles';
import { isRasterDataset } from '../lib/raster-api';
import { hasGeoref, buildDatasetLayers, featureBounds, isVectorFileDataset } from './useDatasetRawLayers';
import ColumnMappingDialog, { mappingStorageKey } from '../components/ColumnMappingDialog';

/**
 * Datasets of the selected project (`/map?project=<id>`) as lazy layers in the layer tree's
 * "Project datasets" group (docs/USER_GUIDE.md, Map). Nothing is downloaded until the user
 * ticks a row: GeoJSON / KML / GPX / CSV come from the signed download URL and go through the
 * same parsers as the Import dialog; raw resistivity surveys reuse the `?dataset=` path
 * (electrodes + pseudosection result layers). Loaded files become `importedLayers` carrying
 * `projectDatasetId`, so rendering, styling, popups and draw order work as for any import.
 */

const GEO_EXTS = ['geojson', 'json', 'kml', 'gpx'];
const TABULAR_EXTS = ['csv', 'txt', 'dat', 'xyz'];
const RAW_EXTS = ['stg', 'stn', 'ohm', 'res2dinv'];

// ── Pure helpers (unit-tested) ──────────────────────────────────────────────

/** Lower-case extension of the dataset's newest stored file (falls back to the name). */
export function datasetExt(ds) {
  const src = ds?.latest_version?.storage_path || ds?.name || '';
  const base = String(src).split('/').pop();
  const i = base.lastIndexOf('.');
  return i < 0 ? '' : base.slice(i + 1).toLowerCase();
}

/**
 * How the map can load a dataset: 'geo' (GeoJSON/KML/GPX), 'tabular' (CSV/XYZ text),
 * 'raw' (parsed resistivity survey), or null (no file, rasters, which have their own path,
 * and formats the map cannot draw).
 */
export function classifyDataset(ds) {
  if (!ds?.latest_version || isRasterDataset(ds)) return null;
  const ext = datasetExt(ds);
  const m = ds.metadata || {};
  if (m.n_readings != null || m.format || RAW_EXTS.includes(ext)) return 'raw';
  if (GEO_EXTS.includes(ext)) return 'geo';
  if (TABULAR_EXTS.includes(ext)) return 'tabular';
  return null;
}

/** Tree-row model for each loadable dataset (server order). */
export function toProjectDatasets(datasets) {
  return (datasets || []).map((d) => ({ id: d.id, name: d.name, kind: classifyDataset(d), method: d.method_type }))
    .filter((d) => d.kind);
}

/**
 * The column mapping saved on the dataset (`column_mapping`, POST /api/datasets/{id}/column-mapping)
 * when it still fits the file's columns, else null.
 */
export function savedDatasetMapping(row, headers) {
  const m = row?.column_mapping;
  if (!m || !(m.x >= 0) || !(m.y >= 0) || m.x >= headers.length || m.y >= headers.length) return null;
  if (Array.isArray(m.headers) && m.headers.length && m.headers.join('|').toLowerCase() !== headers.join('|').toLowerCase()) return null;
  return { extras: {}, format: 'dd', crs: 'EPSG:4326', ...m, z: Array.isArray(m.z) ? m.z : [] };
}

/** The body saved on the dataset for a mapping applied in the dialog (V.26). */
export function mappingForSave(mapping, analyzed) {
  const { x, y, z, activeZ, crs, format, extras = {} } = mapping;
  const cleanExtras = Object.fromEntries(Object.entries(extras).filter(([, v]) => Number.isInteger(v) && v >= 0));
  return {
    x, y, z, activeZ: activeZ ?? z?.[0] ?? null, crs, format, extras: cleanExtras, headers: analyzed.headers,
    delimiter: analyzed.delimiter ?? null,
    skipRows: Number.isInteger(analyzed.headerRow) ? analyzed.headerRow + 1 : null,
  };
}

/** The remembered or confidently detected column mapping for a parsed table, or null. */
export function pickMapping(analyzed, storage = (typeof localStorage !== 'undefined' ? localStorage : null)) {
  let saved = null;
  try { saved = JSON.parse(storage?.getItem(mappingStorageKey(analyzed.headers)) || 'null'); } catch { /* ignore */ }
  if (saved && saved.x >= 0 && saved.y >= 0) {
    if (saved.crs === 'UTM_AUTO') {
      const hit = resolveUtmFromTable(analyzed.headers, analyzed.rows, saved.x, saved.y, '');
      if (hit) return { ...saved, crs: hit.code };
    } else {
      return saved;
    }
  }
  const d = analyzed.detection || {};
  // A projected file with no resolved zone must not be plotted: the old default
  // was UTM 17N, which put other zones in the wrong place.
  if (d.confidence >= 0.8 && d.x >= 0 && d.y >= 0 && d.z?.length > 0 && d.crs) {
    return {
      x: d.x, y: d.y, z: d.z.slice(0, 4), activeZ: d.z[0], crs: d.crs,
      format: d.crs === 'EPSG:4326' ? 'dd' : 'utm', extras: {}, headers: analyzed.headers,
    };
  }
  return null;
}

const errText = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

// ── Hook ────────────────────────────────────────────────────────────────────

/**
 * `datasetId` (`/map?dataset=<id>`, "View on Map" on the dataset page): a GeoJSON/KML/GPX/CSV
 * dataset is fetched, added to the list and loaded straight away, then the map fits to it.
 * Raw surveys keep their own path (hooks/useDatasetRawLayers).
 *
 * Returns { datasets, status, message, load, mappingElement }.
 * `datasets` are { id, name, kind, method }; `status[id]` is 'loading' | 'error' | 'needs-georef'
 * | 'needs-mapping' (absent when idle or loaded); `message[id]` explains it.
 */
export function useProjectDatasetLayers({ projectId, datasetId = null, layerIds = [], setImportedLayers, setResultLayers, mapRefHolder, overlayOpen = false }) {
  const [raw, setRaw] = useState([]);
  const [status, setStatus] = useState({});
  const [message, setMessage] = useState({});
  const [mappingRequest, setMappingRequest] = useState(null);
  const { showToast } = useOptionalToast();
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const datasets = useMemo(() => toProjectDatasets(raw), [raw]);
  const linkedRow = useRef([]); // the ?dataset= / ?layers= rows, kept when the project list replaces `raw`
  const layerKey = layerIds.join(',');

  useEffect(() => {
    setRaw([]); setStatus({}); setMessage({}); setMappingRequest(null);
    if (!projectId) return undefined;
    let active = true;
    datasetsApi.list({ project_id: projectId, page_size: 200 })
      .then((res) => {
        if (!active) return;
        const list = res.data?.datasets || [];
        const extra = linkedRow.current.filter((r) => !list.some((d) => d.id === r.id));
        setRaw([...list, ...extra]);
      })
      .catch((e) => { if (active) console.warn('Failed to list project datasets:', e); });
    return () => {
      active = false;
      // Layers of the previous project must not linger when the project changes.
      setImportedLayers((ls) => ls.filter((l) => !l.projectDatasetId));
      setResultLayers((ls) => ls.filter((r) => !r.projectDatasetId));
    };
  }, [projectId, setImportedLayers, setResultLayers]);

  // `?dataset=<id>` / `?layers=<id>,...` naming vector files: add their rows to the list, then
  // load each once. A `?layers=` id that leaves the URL takes its imported layer with it.
  const [autoLoad, setAutoLoad] = useState([]);
  const prevLayerIds = useRef([]);
  const prevPrimary = useRef(null);
  useEffect(() => {
    const current = layerKey ? layerKey.split(',') : [];
    const ids = [datasetId, ...current].filter(Boolean);
    const gone = prevLayerIds.current.filter((id) => !current.includes(id));
    prevLayerIds.current = current;
    if (gone.length) {
      setImportedLayers((ls) => ls.filter((l) => !gone.includes(l.projectDatasetId)));
      setResultLayers((ls) => ls.filter((r) => !gone.includes(r.projectDatasetId)));
      linkedRow.current = linkedRow.current.filter((r) => !gone.includes(r.id));
    }
    if (!ids.length) { setAutoLoad([]); linkedRow.current = []; return undefined; }
    let active = true;
    const primaryUnchanged = prevPrimary.current === datasetId;
    prevPrimary.current = datasetId;
    ids.forEach((id) => {
      if (linkedRow.current.some((r) => r.id === id) && (id !== datasetId || primaryUnchanged)) return;
      datasetsApi.get(id)
        .then((res) => {
          const row = res.data;
          if (!active || !isVectorFileDataset(row) || !row.latest_version) return;
          linkedRow.current = [...linkedRow.current.filter((r) => r.id !== row.id), row];
          setRaw((prev) => (prev.some((d) => d.id === row.id) ? prev : [...prev, row]));
          setAutoLoad((q) => [...q, row.id]);
        })
        .catch((e) => { if (active) console.warn('Failed to read the dataset:', e); });
    });
    return () => { active = false; };
  }, [datasetId, layerKey, setImportedLayers, setResultLayers]);

  const set = useCallback((id, st, msg) => {
    if (!mounted.current) return;
    setStatus((s) => { const n = { ...s }; if (st) n[id] = st; else delete n[id]; return n; });
    setMessage((m) => ({ ...m, [id]: msg || null }));
  }, []);

  const { syncCamera } = useViewSync();
  const syncCameraRef = useRef(false);
  syncCameraRef.current = syncCamera;

  // Waits (up to ~6 s) for the map instance: `?dataset=` loads can finish before the map mounts.
  const fit = useCallback((bounds) => {
    if (!bounds || syncCameraRef.current) return; // Sync camera: the synced camera wins over the zoom-to-fit (TODO P.20)
    let tries = 0;
    const apply = () => {
      const map = mapRefHolder?.current?.current?.getMap?.();
      if (!map) return ++tries <= 40;
      if (bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]) map.jumpTo({ center: bounds[0], zoom: 16 });
      else map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 18 });
      return false;
    };
    if (!apply()) return;
    const t = setInterval(() => { if (!apply()) clearInterval(t); }, 150);
  }, [mapRefHolder]);

  const addImported = useCallback((ds, geojson) => {
    if (!geojson?.features?.length) throw new Error('No features found in this dataset');
    const xyz = geojson.metadata?.xyz || null;
    if (!mounted.current) return;
    setImportedLayers((prev) => [...prev.filter((l) => l.projectDatasetId !== ds.id), {
      id: `pds-${ds.id}`, name: ds.name, geojson, visible: true, xyz, activeZ: xyz?.activeZ || null, projectDatasetId: ds.id,
    }]);
    set(ds.id, null);
    // V.46: say how many rows were dropped (also listed in the layer's Info tab)
    const skipped = describeSkipped(xyz);
    if (skipped) showToast(`${ds.name}: ${geojson.features.length.toLocaleString()} points plotted, ${skipped}`, 'warning', 8000);
    fit(geojsonBounds(geojson));
  }, [setImportedLayers, set, fit, showToast]);

  // V.26: keep the applied mapping on the dataset so a reload does not ask again (best effort:
  // viewers cannot save, and an old backend without the route must not block plotting).
  const saveMapping = useCallback((ds, mapping, analyzed) => {
    const body = mappingForSave(mapping, analyzed);
    setRaw((prev) => prev.map((d) => (d.id === ds.id ? { ...d, column_mapping: body } : d)));
    linkedRow.current = linkedRow.current.map((r) => (r.id === ds.id ? { ...r, column_mapping: body } : r));
    if (!datasetsApi.saveColumnMapping) return;
    Promise.resolve(datasetsApi.saveColumnMapping(ds.id, body))
      .catch((e) => console.warn('Could not save the column mapping on the dataset:', e));
  }, []);

  const loadRaw = useCallback(async (ds, row) => {
    if (!hasGeoref(row?.metadata)) {
      set(ds.id, 'needs-georef', 'Georeference this survey to place it on the map (open it from Datasets).');
      return;
    }
    if (chooseLayerMode(row.metadata) === 'vector') {
      set(ds.id, 'error', 'Large survey: open it with the dataset link (vector tiles).');
      return;
    }
    const [el, ps] = await Promise.all([rawApi.electrodes(ds.id), rawApi.pseudosectionAll(ds.id, { qc: 1 })]);
    if (!mounted.current) return;
    const layers = buildDatasetLayers(ds.id, ds.name, el.data, ps.data).map((l) => ({ ...l, projectDatasetId: ds.id }));
    setResultLayers((prev) => [...prev.filter((r) => r.dataset?.id !== ds.id), ...layers]);
    set(ds.id, null);
    fit(featureBounds([el.data, ps.data]));
  }, [setResultLayers, set, fit]);

  const load = useCallback(async (id) => {
    const row = raw.find((d) => d.id === id);
    const ds = datasets.find((d) => d.id === id);
    if (!row || !ds) return;
    set(id, 'loading');
    try {
      if (ds.kind === 'raw') { await loadRaw(ds, row); return; }
      const dl = await datasetsApi.getDownloadUrl(id);
      const resp = await fetch(dl.data.download_url); // signed storage URL: no auth header
      if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
      const text = await resp.text();
      if (ds.kind === 'geo') {
        addImported(ds, parseGeoText(datasetExt(row), text));
        return;
      }
      const hint = `${ds.name || ''} ${row.latest_version?.storage_path || ''}`;
      const analyzed = analyzeTabularFile(text, hint);
      // V.42: header-only / empty files fail here instead of waiting on a mapping that cannot plot
      if (analyzed.rows.length === 0) {
        set(id, 'error', analyzed.headers.length ? 'The file has no data rows' : 'The file is empty');
        return;
      }
      const mapping = savedDatasetMapping(row, analyzed.headers) || pickMapping(analyzed);
      let geojson = null; let problem = null;
      if (mapping) {
        try { geojson = buildXYZGeoJSON(analyzed.headers, analyzed.rows, { ...mapping, hint }); } catch (e) { problem = errText(e, 'The saved column mapping does not fit this file'); }
      }
      if (geojson) addImported(ds, geojson);
      else {
        set(id, 'needs-mapping', problem || 'Map the X/Y/Z columns to place this file.');
        if (mounted.current) setMappingRequest({ ds, analyzed, hint, mapping, problem });
      }
    } catch (e) {
      set(id, 'error', errText(e, 'Could not load this dataset on the map'));
    }
  }, [raw, datasets, set, loadRaw, addImported]);

  useEffect(() => {
    const next = autoLoad.find((id) => datasets.some((d) => d.id === id));
    if (!next) return;
    setAutoLoad((q) => q.filter((id) => id !== next));
    load(next);
  }, [autoLoad, datasets, load]);

  // Queued while a full-screen overlay (Report Builder, Model Builder, ...) is open: the dialog
  // appears once it closes instead of covering the overlay and eating its clicks (V.27).
  const mappingElement = mappingRequest && !overlayOpen ? (
    <ColumnMappingDialog
      fileName={mappingRequest.ds.name}
      hint={mappingRequest.hint}
      headers={mappingRequest.analyzed.headers}
      rows={mappingRequest.analyzed.rows}
      detection={mappingRequest.mapping
        ? { ...mappingRequest.analyzed.detection, x: mappingRequest.mapping.x, y: mappingRequest.mapping.y, z: mappingRequest.mapping.z, crs: mappingRequest.mapping.crs }
        : mappingRequest.analyzed.detection}
      initialError={mappingRequest.problem}
      onApply={(m, built) => {
        const { ds, analyzed } = mappingRequest;
        setMappingRequest(null);
        try {
          const layer = built || buildXYZGeoJSON(analyzed.headers, analyzed.rows, m);
          addImported(ds, layer);
          // Save the zone UTM auto resolved to, so /points and the next load agree
          saveMapping(ds, { ...m, crs: layer.metadata?.xyz?.crs || m.crs }, analyzed);
        } catch (e) { set(ds.id, 'error', errText(e, 'Could not build the layer')); }
      }}
      onClose={() => setMappingRequest(null)}
    />
  ) : null;

  // A dataset just stored from the map (Layers panel > Save...): list it and draw it, no reload.
  const addDataset = useCallback((row, geojson) => {
    if (!row?.id) return;
    setRaw((prev) => (prev.some((d) => d.id === row.id) ? prev.map((d) => (d.id === row.id ? row : d)) : [...prev, row]));
    try { addImported({ id: row.id, name: row.name }, geojson); } catch (e) { set(row.id, 'error', errText(e, 'Could not draw the saved layer')); }
  }, [addImported, set]);

  return { datasets, status, message, load, addDataset, mappingElement };
}
